/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { readStorage, removeStorage, writeStorage } from "../../lib/storage";

export type ColumnSpec = {
  key: string;
  width?: number;
  minWidth?: number;
  pinned?: boolean;
  hidden?: boolean;
};

/** What a viewer has made of a table: the column order, which are hidden or pinned, and widths. */
export type TableState = {
  order: string[];
  hidden: string[];
  pinned: string[];
  widths: Record<string, number>;
};

const STORAGE_PREFIX = "table:";
export const MIN_COLUMN_WIDTH = 72;

export function defaultState(columns: ColumnSpec[]): TableState {
  return {
    order: columns.map((column) => column.key),
    hidden: columns.filter((column) => column.hidden).map((column) => column.key),
    pinned: columns.filter((column) => column.pinned).map((column) => column.key),
    widths: Object.fromEntries(
      columns.filter((column) => column.width).map((column) => [column.key, column.width ?? 0]),
    ),
  };
}

/**
 * A saved state against today's columns: unknown keys dropped, and columns the viewer has never
 * seen appended with their own defaults.
 */
export function reconcile(saved: TableState, columns: ColumnSpec[]): TableState {
  const known = new Set(columns.map((column) => column.key));
  const seen = new Set(saved.order);
  const fresh = defaultState(columns.filter((column) => !seen.has(column.key)));
  return {
    order: [...saved.order.filter((key) => known.has(key)), ...fresh.order],
    hidden: [...saved.hidden.filter((key) => known.has(key)), ...fresh.hidden],
    pinned: [...saved.pinned.filter((key) => known.has(key)), ...fresh.pinned],
    widths: {
      ...Object.fromEntries(
        Object.entries(saved.widths).filter(([key, width]) => known.has(key) && width > 0),
      ),
      ...fresh.widths,
    },
  };
}

/** The columns as rendered: pinned ones first in their order, then the rest, hidden ones out. */
export function visibleOrder(state: TableState): string[] {
  const shown = state.order.filter((key) => !state.hidden.includes(key));
  return [
    ...shown.filter((key) => state.pinned.includes(key)),
    ...shown.filter((key) => !state.pinned.includes(key)),
  ];
}

export function toggleHidden(state: TableState, key: string): TableState {
  const hidden = state.hidden.includes(key)
    ? state.hidden.filter((other) => other !== key)
    : [...state.hidden, key];
  return { ...state, hidden };
}

export function togglePinned(state: TableState, key: string, width: number): TableState {
  if (state.pinned.includes(key)) {
    return { ...state, pinned: state.pinned.filter((other) => other !== key) };
  }
  return {
    ...state,
    pinned: [...state.pinned, key],
    widths: { ...state.widths, [key]: state.widths[key] ?? width },
  };
}

export function setWidth(state: TableState, key: string, width: number, minWidth: number): TableState {
  return { ...state, widths: { ...state.widths, [key]: Math.max(minWidth, Math.round(width)) } };
}

/** Puts a column before another (or last when before is null), keeping pinned and unpinned apart. */
export function placeBefore(state: TableState, key: string, before: string | null): TableState {
  if (key === before) {
    return state;
  }
  if (before !== null && state.pinned.includes(before) !== state.pinned.includes(key)) {
    return state;
  }
  const order = state.order.filter((other) => other !== key);
  const at = before === null ? order.length : order.indexOf(before);
  order.splice(at, 0, key);
  return { ...state, order };
}

export function loadState(storageKey: string, columns: ColumnSpec[]): TableState {
  const raw = readStorage(STORAGE_PREFIX + storageKey);
  if (!raw) {
    return defaultState(columns);
  }
  try {
    const parsed = JSON.parse(raw) as Partial<TableState>;
    return reconcile(
      {
        order: Array.isArray(parsed.order) ? parsed.order : [],
        hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
        pinned: Array.isArray(parsed.pinned) ? parsed.pinned : [],
        widths: parsed.widths && typeof parsed.widths === "object" ? parsed.widths : {},
      },
      columns,
    );
  } catch {
    return defaultState(columns);
  }
}

export function saveState(storageKey: string, state: TableState): void {
  writeStorage(STORAGE_PREFIX + storageKey, JSON.stringify(state));
}

export function clearState(storageKey: string): void {
  removeStorage(STORAGE_PREFIX + storageKey);
}
